import type { IntegrationField } from '../../api/integrations.api';

import type { CurrencyMapValue } from './CurrencyMapField';

/**
 * What a settings form is about to change, for the save bar's review and its
 * reason prompt (DEV-133). Mirrors the API's rule (settings-change-risk.ts):
 * the client-money and revenue accounts and any credential need a reason, and
 * so does switching commission transfers to LIVE. The server enforces it; the
 * console asks first so nobody meets a refusal.
 */

export const REASON_REQUIRED_KEYS: readonly string[] = ['masterAccountUuids', 'revenueAccountUuids'];

export interface PendingChange {
  readonly key: string;
  readonly label: string;
  readonly before: unknown;
  readonly after: unknown;
  /** A credential: shown as "changed", never by value. */
  readonly secret: boolean;
  /** Needs a stated reason. */
  readonly needsReason: boolean;
}

/** JSON with object keys sorted, so a re-ordered map is not a change. */
function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (typeof value === 'object' && value !== null) {
    return `{${Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`)
      .join(',')}}`;
  }
  return JSON.stringify(value ?? null);
}

const blank = (value: unknown): boolean => value === null || value === undefined || value === '';

export function pendingChanges(
  fields: readonly IntegrationField[],
  values: Readonly<Record<string, string>>,
  maps: Readonly<Record<string, CurrencyMapValue>>,
): PendingChange[] {
  const changes: PendingChange[] = [];
  for (const field of fields) {
    if (field.secret) {
      const entered = values[field.key];
      // An untouched or blank secret keeps the stored one and is not sent.
      if (entered !== undefined && entered !== '') {
        changes.push({ key: field.key, label: field.label, before: null, after: null, secret: true, needsReason: true });
      }
      continue;
    }
    const after = field.kind === 'currency-map' ? maps[field.key] : values[field.key];
    if (after === undefined) continue;
    const before = field.value;
    const scalars = typeof after !== 'object' && (typeof before !== 'object' || before === null);
    const same = scalars
      ? String(blank(after) ? '' : after) === String(blank(before) ? '' : before)
      : canonical(blank(after) ? null : after) === canonical(blank(before) ? null : before);
    if (same) continue;
    const toLive = field.key === 'commissionTransferMode' && after === 'LIVE';
    changes.push({ key: field.key, label: field.label, before, after, secret: false,
      needsReason: REASON_REQUIRED_KEYS.includes(field.key) || toLive });
  }
  return changes;
}

/** A value as the review shows it: maps as "EUR: …, GBP: …", empty as "—". */
export function describeValue(value: unknown): string {
  if (blank(value)) return '—';
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return '—';
    return entries.map(([k, v]) => `${k}: ${typeof v === 'object' && v !== null ? Object.values(v).filter(Boolean).join(' / ') : String(v)}`).join(', ');
  }
  return String(value);
}
